import { PieChart } from '@mantine/charts'
import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Countdown from '@/components/Countdown'
import ErrorMessage from '@/components/ErrorMessage'
import Loading from '@/components/Loading'
import { useEconomicsContext } from '@/contexts/EconomicsContext'
import { processBalance, ProcessedBalance } from '@/utils/utils'

import MSTClock from './MSTClock'

const ChartLegendRow: React.FC<{ value: string; color: string; text: string }> = ({
  value,
  color,
  text,
}) => (
  <Stack style={{ flexDirection: 'row', gap: '15px', alignItems: 'center' }}>
    <div
      style={{
        minWidth: '11px',
        minHeight: '11px',
        borderRadius: '3px',
        backgroundColor: color,
      }}
    />
    <Text>{`${text} (${value})`}</Text>
  </Stack>
)

interface ChartLegendProps {
  rl1Minted: ProcessedBalance
  title: string
  data: { name: string; value: number; color: string }[]
}

const ChartLegend: React.FC<ChartLegendProps> = ({ rl1Minted, title, data }) => (
  <Stack style={{ gap: '15px' }}>
    <Title order={4}>{title}</Title>
    <Stack style={{ gap: '10px' }}>
      {data.map(item => {
        const value = processBalance(
          BigInt(Math.round(parseFloat(rl1Minted.value.toString()) * (item.value / 100)))
        )
        return (
          <ChartLegendRow
            key={item.name}
            value={value.display}
            color={item.color}
            text={item.name}
          />
        )
      })}
    </Stack>
  </Stack>
)

const RewardsRecipients: React.FC = () => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isDownMd = useMediaQuery(`(max-width: 1130px)`)

  const {
    getCurrentDay,
    getDayStart,
    rl1CurrentPeriod,
    rl1CurrentPeriodStart,
    rl1CurrentPeriodEnd,
    rl1Minted,
    rl1SupplyLeft,
    rewardsPercentages,
    isLoading,
    error,
  } = useEconomicsContext()

  // Convert period start and end to dates
  const periodStart = useMemo(
    () => new Date(rl1CurrentPeriodStart * 1000).toLocaleDateString(),
    [rl1CurrentPeriodStart]
  )
  const periodEnd = useMemo(
    () => new Date(rl1CurrentPeriodEnd * 1000).toLocaleDateString(),
    [rl1CurrentPeriodEnd]
  )

  const data = useMemo(() => {
    if (isLoading || error.isError || rewardsPercentages.length < 3) {
      return []
    }
    const values = []
    if (rewardsPercentages[0] > 0) {
      values.push({
        name: t('components.staking.staking.barChart.stakers', 'Stakers') as string,
        value: Number((100 * rewardsPercentages[0]).toPrecision(2)),
        color: theme.colors.barChartGreen[0],
      })
    }
    if (rewardsPercentages[1] > 0) {
      values.push({
        name: t('components.staking.staking.barChart.creators', 'Creators') as string,
        value: Number((100 * rewardsPercentages[1]).toPrecision(2)),
        color: theme.colors.barChartPurple[0],
      })
    }
    if (rewardsPercentages[2] > 0) {
      values.push({
        name: t('components.staking.staking.barChart.ecosystem', 'Ecosystem') as string,
        value: Number((100 * rewardsPercentages[2]).toPrecision(2)),
        color: theme.colors.barChartBlue[0],
      })
    }
    return values
  }, [isLoading, error, rewardsPercentages, t, theme])

  const issuancePercentage = useMemo(() => {
    if (rl1Minted && rl1SupplyLeft) {
      return (
        (100 * parseFloat(rl1Minted.value.toString())) / parseFloat(rl1SupplyLeft.value.toString())
      )
    }
    return 0
  }, [rl1Minted, rl1SupplyLeft])

  // Trigger update day
  const [trigger, setTrigger] = useState<boolean>(false)
  useEffect(() => {
    const interval = setInterval(() => {
      setTrigger(prev => !prev)
    }, 1000)
    return () => clearInterval(interval)
  }, [])
  const today = useMemo(() => getCurrentDay(), [getCurrentDay, trigger])
  const nextDayStart = useMemo(() => getDayStart(today + 1), [getDayStart, today])

  return (
    <Stack style={{ gap: '15px', minWidth: '350px' }}>
      <Title order={2}>{t('components.staking.staking.rewardsInfo.title', 'Rewards Info')}</Title>
      {error.isError && !isLoading && (
        <ErrorMessage
          message={t(
            'components.staking.staking.loadingEconomics',
            'RPC Error loading {{ call }}',
            {
              call: error.call,
            }
          )}
          details={error.msg}
        />
      )}
      {isLoading && !error.isError && <Loading />}
      {!isLoading && !error.isError && (
        <Stack style={{ flexGrow: 1, gap: '30px' }}>
          <Stack
            style={{ gap: isDownMd ? '20px' : '0px', flexDirection: isDownMd ? 'column' : 'row' }}
          >
            <Stack style={{ gap: '0px' }}>
              <Title order={4} style={{ marginBottom: '5px' }}>
                Current Rewards Cycle
              </Title>
              <Stack style={{ flexDirection: 'row', gap: '5px' }}>
                <Text style={{ fontFamily: 'monument-grotesk-heavy' }}>Day:</Text>
                <Text>{today}</Text>
              </Stack>
              <Stack style={{ flexDirection: 'row', gap: '5px' }}>
                <Text style={{ fontFamily: 'monument-grotesk-heavy' }}>Quarter:</Text>
                <Text>{rl1CurrentPeriod + 1}</Text>
              </Stack>
              <Stack style={{ flexDirection: 'row', gap: '5px' }}>
                <Text style={{ fontFamily: 'monument-grotesk-heavy' }}>Period:</Text>
                <Text>
                  {periodStart} - {periodEnd}
                </Text>
              </Stack>
              <Stack style={{ flexDirection: 'row', gap: '5px' }}>
                <Text style={{ fontFamily: 'monument-grotesk-heavy' }}>Issuance:</Text>
                <Text>
                  {rl1Minted?.display || '-'} RL1 ({issuancePercentage.toFixed(0)}% of{' '}
                  {rl1SupplyLeft.display || '-'} RL1)
                </Text>
              </Stack>
            </Stack>
            <Stack style={{ gap: '0px', alignItems: isDownMd ? 'flex-start' : 'flex-end' }}>
              <Title order={4} style={{ marginBottom: '5px' }}>
                Metaverse Standard Time
              </Title>
              <Stack style={{ gap: '0px', alignItems: isDownMd ? 'flex-start' : 'flex-end' }}>
                <MSTClock />
                {!isDownMd && <br />}
                <Text>Time until next day</Text>
                <Countdown futureDate={nextDayStart} />
              </Stack>
            </Stack>
          </Stack>

          <Stack
            style={{
              alignItems: 'center',
              gap: '30px',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <PieChart
              data={data}
              labelsPosition="inside"
              labelsType="percent"
              withLabels
              strokeWidth={0}
              size={200}
            />
            <ChartLegend
              rl1Minted={rl1Minted}
              title={t(
                'components.staking.staking.rewardsRecipients.rewardsPool',
                'Rewards Pool Allocation'
              )}
              data={data}
            />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default RewardsRecipients
